import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const VideoHero = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  // Parallax effects
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.8]);

  return (
    <section ref={containerRef} id="video-hero" className="relative w-full overflow-hidden h-[70vw] md:h-screen">
      {/* YouTube Video Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <iframe
          className="absolute top-1/2 left-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full -translate-x-1/2 -translate-y-1/2"
          src="https://www.youtube.com/embed/fVjXZTMIB_A?si=s0kDRJ88q86JO0fx&autoplay=1&mute=1&loop=1&playlist=fVjXZTMIB_A&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&vq=hd1080"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>

      {/* Dark overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-steel/70 via-steel/50 to-steel/70"
        style={{ opacity: overlayOpacity }}
      ></motion.div>

      {/* Content with parallax movement */}
      <motion.div
        className="relative z-10 h-full flex items-center pt-20"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <div className="max-w-7xl mx-auto px-6 w-full">
          <motion.div
            className="text-center space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Scroll indicator with wave text animation */}
            <motion.div
              className="hidden md:flex flex-col items-center absolute bottom-10 left-1/2 transform -translate-x-1/2 cursor-pointer z-20 gap-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 0.85, y: 0 }}
              transition={{
                duration: 1,
                delay: 1.2
              }}
              onClick={() => {
                window.scrollBy({
                  top: window.innerHeight,
                  behavior: 'smooth'
                });
              }}
              whileHover={{ scale: 1.05, opacity: 1 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Wave Text Animation */}
              <div className="flex overflow-hidden">
                {"SCROLL".split("").map((letter, index) => (
                  <motion.span
                    key={index}
                    className="text-white text-sm font-semibold tracking-[0.3em] uppercase"
                    animate={{
                      y: [0, -4, 0],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: index * 0.08,
                      ease: "easeInOut"
                    }}
                  >
                    {letter}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom scroll indicator (mobile) */}
      <motion.div
        className="absolute bottom-4 left-0 right-0 z-20 flex flex-col items-center md:hidden cursor-pointer"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        onClick={() => {
          window.scrollBy({
            top: window.innerHeight,
            behavior: 'smooth'
          });
        }}
      >
        <span className="text-white/90 text-xs font-semibold tracking-[0.25em] uppercase mb-1">Scroll Down</span>
        <motion.svg
          className="w-5 h-5 text-white/80"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </motion.div>
    </section>
  );
};

export default VideoHero;